import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  User,
  Mail,
  GraduationCap,
  MapPin,
  Compass,
  ArrowRight,
  AlertCircle,
  Loader2,
  Lock,
} from "lucide-react";

import { StudentRegistrationFormSchema, INDIAN_STATES } from "@/types/workshop";
import supabase from "@/api/supabaseClient";
import { workshopsService } from "@/api/workshopsService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ResumeUploader from "./ResumeUploader";

/**
 * ============================================================
 * WORKSHOP REGISTRATION FORM COMPONENT
 * ============================================================
 * Strictly renders the five required student fields:
 * 1. Full Name
 * 2. Resume (validated file uploaded to private Supabase Storage)
 * 3. College / University
 * 4. Residing City
 * 5. Residing State (controlled Indian states list)
 *
 * PROHIBITED: Email, phone, address, LinkedIn, GitHub, password, etc.
 * Resumes are stored privately in `workshop-resumes` bucket with zero public access.
 */
export default function WorkshopRegistrationForm({ workshop, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitError, setSubmitError] = useState("");

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(StudentRegistrationFormSchema),
    defaultValues: {
      name: "",
      email: "",
      resume: null,
      college: "",
      residing_city: "",
      residing_state: "",
    },
  });

  const currentResume = watch("resume");

  const onSubmit = async (data) => {
    setSubmitError("");
    setSubmitting(true);
    setUploadProgress(15);

    try {
      // Step 1: Upload resume to private Supabase Storage (workshop-resumes)
      let storagePath = "";
      const resumeFilename = data.resume?.name || "resume.pdf";

      if (data.resume instanceof File) {
        const progressTimer = setInterval(() => {
          setUploadProgress((prev) => (prev < 85 ? prev + 15 : prev));
        }, 150);

        try {
          const uniqueId =
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          const sanitizedFilename = data.resume.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const targetPath = `resumes/${workshop.id}/${uniqueId}_${sanitizedFilename}`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from("workshop-resumes")
            .upload(targetPath, data.resume, {
              cacheControl: "3600",
              upsert: false,
            });

          if (uploadError) {
            if (process.env.NODE_ENV !== "production" && uploadError.message?.includes("fetch")) {
              console.warn(
                "[WorkshopRegistrationForm] Dev simulation for resume storage:",
                uploadError.message
              );
              storagePath = targetPath;
            } else {
              throw new Error(`Resume upload failed: ${uploadError.message}`);
            }
          } else {
            storagePath = uploadData?.path || targetPath;
          }
        } finally {
          clearInterval(progressTimer);
        }
        setUploadProgress(95);
      }

      setUploadProgress(100);

      // Step 2: Submit registration record to service layer (RPC register_for_workshop)
      await workshopsService.submitRegistration(
        workshop.id,
        data,
        storagePath,
        resumeFilename
      );

      // Step 3: Trigger success flow
      onSuccess?.(data.name.trim());
    } catch (err) {
      console.error("Workshop registration failed:", err);
      setSubmitError(
        err.message ||
        "Failed to submit your registration. Please check your connection and try again."
      );
      setUploadProgress(0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-2xl p-6 sm:p-8 lg:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] space-y-6">
        {/* Form Title & Header */}
        <div className="space-y-1.5 border-b border-white/10 pb-5">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
            Student Application Form
          </h2>
          <p className="text-xs sm:text-sm text-white/60">
            Please provide your details below. All 5 fields are required.
          </p>
        </div>

        {/* Global Submit Error Banner */}
        {submitError && (
          <div
            role="alert"
            className="flex items-start gap-3 p-4 rounded-2xl border border-destructive/30 bg-destructive/10 text-xs sm:text-sm text-destructive font-medium"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold">Unable to complete registration</p>
              <p className="text-white/80">{submitError}</p>
            </div>
          </div>
        )}

        {/* Registration Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {/* Row 1: Full Name & Email Address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Field 1: Full Name */}
            <div className="space-y-2">
              <Label htmlFor="student-name" className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-primary" />
                <span>Full Name *</span>
              </Label>
              <Input
                id="student-name"
                type="text"
                placeholder="e.g. Aarav Sharma"
                disabled={submitting}
                autoComplete="name"
                {...register("name")}
                className={`h-11 rounded-xl bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary ${errors.name ? "border-destructive focus-visible:ring-destructive" : ""
                  }`}
              />
              {errors.name && (
                <p className="text-[11px] text-destructive font-medium">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Field 2: Email Address */}
            <div className="space-y-2">
              <Label htmlFor="student-email" className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-primary" />
                <span>Email Address *</span>
              </Label>
              <Input
                id="student-email"
                type="email"
                placeholder="e.g. aarav.sharma@example.com"
                disabled={submitting}
                autoComplete="email"
                {...register("email")}
                className={`h-11 rounded-xl bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary ${errors.email ? "border-destructive focus-visible:ring-destructive" : ""
                  }`}
              />
              {errors.email && (
                <p className="text-[11px] text-destructive font-medium">
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          {/* Field 2: Resume Upload */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
              <span>Resume / CV (PDF or DOC/DOCX, max 5MB) *</span>
            </Label>
            <ResumeUploader
              file={currentResume}
              onFileSelect={(file) => setValue("resume", file, { shouldValidate: true })}
              onFileRemove={() => setValue("resume", null, { shouldValidate: true })}
              error={errors.resume?.message}
              uploading={submitting && uploadProgress > 0}
              uploadProgress={uploadProgress}
              disabled={submitting}
            />
          </div>

          {/* Field 3: College / University */}
          <div className="space-y-2">
            <Label htmlFor="student-college" className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
              <GraduationCap className="w-3.5 h-3.5 text-primary" />
              <span>College / University / Institute *</span>
            </Label>
            <Input
              id="student-college"
              type="text"
              placeholder="e.g. Indian Institute of Technology (BHU) Varanasi"
              disabled={submitting}
              {...register("college")}
              className={`h-11 rounded-xl bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary ${errors.college ? "border-destructive focus-visible:ring-destructive" : ""
                }`}
            />
            {errors.college && (
              <p className="text-[11px] text-destructive font-medium">
                {errors.college.message}
              </p>
            )}
          </div>

          {/* Fields 4 & 5: City & State (Two-column on sm+) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Field 4: Residing City */}
            <div className="space-y-2">
              <Label htmlFor="student-city" className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-primary" />
                <span>City *</span>
              </Label>
              <Input
                id="student-city"
                type="text"
                placeholder="e.g. Varanasi, Noida, Bengaluru"
                disabled={submitting}
                autoComplete="address-level2"
                {...register("residing_city")}
                className={`h-11 rounded-xl bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary ${errors.residing_city ? "border-destructive focus-visible:ring-destructive" : ""
                  }`}
              />
              {errors.residing_city && (
                <p className="text-[11px] text-destructive font-medium">
                  {errors.residing_city.message}
                </p>
              )}
            </div>

            {/* Field 5: Residing State */}
            <div className="space-y-2">
              <Label htmlFor="student-state" className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5 text-primary" />
                <span>State *</span>
              </Label>
              <Controller
                name="residing_state"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={submitting}
                  >
                    <SelectTrigger
                      id="student-state"
                      className={`h-11 rounded-xl bg-white/[0.03] border-white/15 text-white focus:ring-primary ${errors.residing_state
                        ? "border-destructive focus:ring-destructive"
                        : ""
                        }`}
                    >
                      <SelectValue placeholder="Select State / UT" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64 bg-black/95 border-white/20 text-white backdrop-blur-xl">
                      {INDIAN_STATES.map((stateName) => (
                        <SelectItem
                          key={stateName}
                          value={stateName}
                          className="focus:bg-primary focus:text-black cursor-pointer"
                        >
                          {stateName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.residing_state && (
                <p className="text-[11px] text-destructive font-medium">
                  {errors.residing_state.message}
                </p>
              )}
            </div>
          </div>

          {/* Privacy Note */}
          <div className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/5 text-[11px] text-white/50">
            <Lock className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            <span>
              Your resume and information are encrypted and used solely for workshop
              admission review.
            </span>
          </div>

          {/* Submit Action Button */}
          <div className="pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-primary text-black hover:bg-primary/90 font-bold text-sm tracking-wide gap-2 shadow-[0_0_24px_rgba(0,194,255,0.4)] hover:shadow-[0_0_30px_rgba(0,194,255,0.65)] transition-all duration-300"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing Application...</span>
                </>
              ) : (
                <>
                  <span>Submit Workshop Application</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
